import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import type { SigeSchrittDokumente } from '../components/planungsphase/types';

/**
 * Liest die mitgeltenden Dokumente des AKTUELLEN SiGe-Plans eines Projekts —
 * fuer die Unterlagen-Checkliste der Planungsphase („im SiGe-Plan").
 *
 * NUR LESEN. Geschrieben wird der SiGe-Plan ausschliesslich in seinem Editor.
 *
 * ═══ WELCHE FASSUNG ═══════════════════════════════════════════════════════
 * Die neueste, nicht geloeschte Zeile aus `sige_projekte` (`created_at desc`,
 * `deleted_at is null`) — derselbe Weg wie der Editor beim Oeffnen, das
 * Handbuch (`fetchPlanfassungen`) und der A+S-Plan (`ladePlanQuelle`). Der
 * Filter auf `deleted_at` ist tragend: `sige_projekte` loescht weich, und
 * ohne ihn galte eine verworfene Fassung als aktueller Plan.
 *
 * ═══ FAIL-OPEN ═══════════════════════════════════════════════════════════
 * Jeder Fehler — Tabelle fehlt, Spalte `mitgeltende_dokumente` fehlt
 * (Migration Nr. 156 noch nicht gefahren), Netz weg, Regel greift — ergibt
 * „keine Treffer aus dem SiGe-Plan" und KEINEN Absturz. Die Checkliste bleibt
 * ohne Plan voll bedienbar; ein Projekt ohne SiGe-Plan ist ein gueltiger
 * Zustand. Der Fehler wird nicht verschluckt, sondern als `lesefehler`
 * zurueckgegeben, damit die Oberflaeche sagen kann, warum die Planspalte leer
 * ist — sonst laese man „steht nicht im Plan", wo es „konnte nicht lesen"
 * heissen muss.
 */

export interface SigePlanDokumente {
    /** `sige_projekte.id` der gelesenen Fassung, `null` ohne Plan. */
    planId: string | null;
    schritte: SigeSchrittDokumente[];
    /** Gesetzt, wenn gelesen werden sollte, aber nicht konnte. */
    lesefehler: string | null;
    /** Bauherr laut Plankopf (`bauherr_name`) — nur gesetzt, wenn eingetragen. */
    bauherr?: string;
}

const LEER: SigePlanDokumente = { planId: null, schritte: [], lesefehler: null };

/** PostgREST deckelt Antworten ohne Meldung bei 1000 Zeilen. */
const SEITE = 1000;

const alsNamen = (v: unknown): string[] =>
    Array.isArray(v)
        ? v.filter((x): x is string => typeof x === 'string').map(x => x.trim()).filter(Boolean)
        : [];

export const fetchSigePlanDokumente = async (projectId: string): Promise<SigePlanDokumente> => {
    if (!projectId || projectId === 'temp-id') return LEER;
    try {
        // `*` statt einer Spaltenliste: der Bauherr (`bauherr_name`) fuer den
        // Kopf des Handbuchs kommt mit, und eine fehlende Spalte koennte so
        // nie die ganze Abfrage — und damit alle Plan-Treffer — kippen.
        const { data: plaene, error } = await supabase
            .from('sige_projekte')
            .select('*')
            .eq('project_id', projectId)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(1);
        if (error) return { ...LEER, lesefehler: describeError(error) };

        const plan = ((plaene || [])[0] as { id?: string; bauherr_name?: unknown } | undefined) || null;
        const planId = plan && plan.id ? plan.id : null;
        if (!planId) return LEER;
        const bauherr = plan && typeof plan.bauherr_name === 'string' ? plan.bauherr_name.trim() : '';
        const mitBauherr = bauherr ? { bauherr } : {};

        const schritte: SigeSchrittDokumente[] = [];
        for (let von = 0; ; von += SEITE) {
            const { data, error: schrittFehler } = await supabase
                .from('sige_arbeitsschritte')
                .select('id, bezeichnung, mitgeltende_dokumente, sortierung')
                .eq('sige_projekt_id', planId)
                .order('sortierung', { ascending: true })
                // Zweites Kriterium: `sortierung` allein ist nicht eindeutig,
                // und eine uneindeutige Sortierung laesst beim Blaettern Zeilen
                // doppelt erscheinen oder ganz ausfallen.
                .order('id', { ascending: true })
                .range(von, von + SEITE - 1);
            if (schrittFehler) return { planId, schritte: [], lesefehler: describeError(schrittFehler), ...mitBauherr };
            const seite = (data || []) as { bezeichnung?: unknown; mitgeltende_dokumente?: unknown }[];
            seite.forEach(z => {
                const dokumente = alsNamen(z.mitgeltende_dokumente);
                if (!dokumente.length) return;
                schritte.push({
                    bezeichnung: typeof z.bezeichnung === 'string' ? z.bezeichnung : '',
                    dokumente,
                });
            });
            if (seite.length < SEITE) break;
        }
        return { planId, schritte, lesefehler: null, ...mitBauherr };
    } catch (e) {
        return { ...LEER, lesefehler: describeError(e) };
    }
};
